/** ActivatorBase and the switch / zone / modifier / timed / dash activators. */
import { GameBehaviour, type GamePlayer } from '../game.ts';
import { BodyType, type SimCollider } from '../world.ts';
import { FrameCounter, FIXED_DT, vnorm, type V2 } from '../unity.ts';
import { bool, list, num } from '../props.ts';
import { playerOf } from './util.ts';
import type { SpriteRef } from '../../../data/gameData.ts';

export abstract class ActivatorBase extends GameBehaviour {
  isActivated = false;
  /** OnActivationChanged subscribers */
  listeners: ((active: boolean) => void)[] = [];

  setActiveState(a: boolean) {
    if (a) this.activate();
    else this.deactivate();
  }
  activate() {
    if (!this.isActivated) {
      this.isActivated = true;
      this.onActivated();
    }
  }
  deactivate() {
    if (this.isActivated) {
      this.isActivated = false;
      this.onDeactivated();
    }
  }
  protected onActivated() {
    for (const l of this.listeners.slice()) l(true);
  }
  protected onDeactivated() {
    for (const l of this.listeners.slice()) l(false);
  }
}

/** resolve a list of activator references */
export function resolveActivators(b: GameBehaviour, refs: any[]): ActivatorBase[] {
  const out: ActivatorBase[] = [];
  for (const r of refs) {
    const a = b.refBehaviour(r, ActivatorBase as any) as ActivatorBase | null;
    if (a && !out.includes(a)) out.push(a);
  }
  return out;
}

const enum SwitchType {
  Normal,
  Toggle,
  OneTimeStayOn,
  OneTimeStayOff,
}

export class SwitchHandler extends ActivatorBase {
  offSprite: SpriteRef | null = null;
  onSprite: SpriteRef | null = null;
  switchType = SwitchType.Normal;
  invert = false;
  isTriggerTouched = false;
  lastIsTriggerTouched = false;
  switchPhysicalState = false;
  oneTimeTriggered = false;
  collider: SimCollider | null = null;
  override noSave = ['offSprite', 'onSprite', 'collider'];

  override awake() {
    const d = this.data;
    const sp = (r: any) => (r?.guid ? this.game.gd.spriteById.get(`${r.guid}:${r.fileID}`) ?? null : null);
    this.offSprite = sp(d.offSprite);
    this.onSprite = sp(d.onSprite);
    this.switchType = num(d.switchType) as SwitchType;
    this.invert = bool(d.invert);
  }

  override start() {
    this.collider = this.go.colliders[0] ?? null;
    this.updateSprite();
    if (this.invert) this.switchPhysicalStateChanged(this.switchPhysicalState, true);
  }

  protected isValidCollider(c: SimCollider): boolean {
    return c.go !== this.go && c.attachedRigidbody?.type === BodyType.Dynamic;
  }

  override fixedUpdate() {
    if (!this.collider) return;
    this.isTriggerTouched = false;
    for (const h of this.world.overlapCollider(this.collider)) {
      if (this.isValidCollider(h)) {
        this.isTriggerTouched = true;
        break;
      }
    }
    if (this.lastIsTriggerTouched !== this.isTriggerTouched) this.updateSwitchState();
    this.lastIsTriggerTouched = this.isTriggerTouched;
  }

  private updateSwitchState() {
    let s = this.switchPhysicalState;
    switch (this.switchType) {
      case SwitchType.Normal:
        s = this.isTriggerTouched;
        break;
      case SwitchType.Toggle:
        if (this.isTriggerTouched) s = !this.switchPhysicalState;
        break;
      case SwitchType.OneTimeStayOn:
      case SwitchType.OneTimeStayOff:
        if (this.isTriggerTouched && !this.oneTimeTriggered) {
          this.oneTimeTriggered = true;
          s = true;
        }
        if (!this.isTriggerTouched && this.switchType === SwitchType.OneTimeStayOff) s = false;
        break;
    }
    if (s !== this.switchPhysicalState) this.switchPhysicalStateChanged(s);
  }

  private switchPhysicalStateChanged(s: boolean, quiet = false) {
    this.switchPhysicalState = s;
    this.setActiveState(s !== this.invert);
    this.updateSprite();
    if (!quiet && this.go.sprite?.enabled) this.sound(s ? 'switchOn' : 'switchOff');
  }

  private updateSprite() {
    const sr = this.go.sprite;
    if (!sr) return;
    const s = this.switchPhysicalState ? this.onSprite : this.offSprite;
    if (s) sr.sprite = s;
  }
}

const enum DashRequirement {
  None,
  AnyDirection,
  Up,
  UpRight,
  Right,
  DownRight,
  Down,
  DownLeft,
  Left,
  UpLeft,
}

export class ZoneActivator extends SwitchHandler {
  onlyPlayerActivates = true;
  requiredDashDirection = DashRequirement.None;
  diagonalDashCountsForCardinal = false;

  override awake() {
    super.awake();
    this.onlyPlayerActivates = bool(this.data.onlyPlayerActivates, true);
    this.requiredDashDirection = num(this.data.requiredDashDirection) as DashRequirement;
    this.diagonalDashCountsForCardinal = bool(this.data.diagonalDashCountsForCardinal);
  }

  protected override isValidCollider(c: SimCollider): boolean {
    const p = playerOf(c);
    if (p) return this.passesDashRequirement(p);
    return !this.onlyPlayerActivates && c.attachedRigidbody?.type === BodyType.Dynamic;
  }

  private passesDashRequirement(p: import('../player.ts').PlayerController): boolean {
    const r = this.requiredDashDirection;
    if (r === DashRequirement.None) return true;
    if (r === DashRequirement.AnyDirection) return p.isDashing();
    if (!p.isDashing()) return false;
    return dashMatches(p.dashDirection, r, this.diagonalDashCountsForCardinal);
  }

  override start() {
    super.start();
    if (this.go.sprite) this.go.sprite.enabled = false;
  }
}

function dashMatches(dir: V2, req: DashRequirement, lenient: boolean): boolean {
  if (dir.x === 0 && dir.y === 0) return false;
  const d = vnorm(dir);
  const cardinal = req === DashRequirement.Up || req === DashRequirement.Right || req === DashRequirement.Down || req === DashRequirement.Left;
  if (lenient && cardinal) {
    if (req === DashRequirement.Up) return d.y > 0;
    if (req === DashRequirement.Down) return d.y < 0;
    if (req === DashRequirement.Right) return d.x > 0;
    return d.x < 0;
  }
  let c = DashRequirement.None;
  if (d.x === 0) {
    if (d.y > 0) c = DashRequirement.Up;
    else if (d.y < 0) c = DashRequirement.Down;
  } else if (d.x > 0) c = d.y > 0 ? DashRequirement.UpRight : d.y < 0 ? DashRequirement.DownRight : DashRequirement.Right;
  else c = d.y > 0 ? DashRequirement.UpLeft : d.y < 0 ? DashRequirement.DownLeft : DashRequirement.Left;
  return c === req;
}

const enum ModifierActivation {
  All,
  Any,
  AllOrNothing,
}

export class ActivatorModifier extends ActivatorBase {
  activators: ActivatorBase[] = [];
  activationType = ModifierActivation.All;
  invert = false;
  automaticallyIncludeChildActivators = true;
  toggleOnActivate = false;
  activated = false;

  override awake() {
    const d = this.data;
    this.activationType = num(d.activationType) as ModifierActivation;
    this.invert = bool(d.invert);
    this.automaticallyIncludeChildActivators = bool(d.automaticallyIncludeChildActivators, true);
    this.toggleOnActivate = bool(d.toggleOnActivate);
  }

  override start() {
    this.activators = resolveActivators(this, list(this.data.activators));
    this.activated = this.isActivated;
    if (this.automaticallyIncludeChildActivators) {
      for (const a of this.go.getBehavioursInChildren(ActivatorBase as any, false) as ActivatorBase[]) {
        if (!this.activators.includes(a) && a !== this) this.activators.push(a);
      }
    }
  }

  override fixedUpdate() {
    const last = this.activated;
    if (this.activators.length === 0) this.activated = true;
    switch (this.activationType) {
      case ModifierActivation.All:
        this.activated = this.activators.every((a) => a.isActivated);
        break;
      case ModifierActivation.Any:
        this.activated = this.activators.some((a) => a.isActivated);
        break;
      case ModifierActivation.AllOrNothing:
        if (this.activated && this.activators.every((a) => !a.isActivated)) this.activated = false;
        else if (this.activators.every((a) => a.isActivated)) this.activated = true;
        break;
      default:
        this.activated = false;
    }
    if (this.invert) this.activated = !this.activated;
    if (this.toggleOnActivate) {
      if (!last && this.activated) this.setActiveState(!this.isActivated);
    } else this.setActiveState(this.activated);
  }
}

const TIMER_SECONDS = [10, 5, 2, 1, 0.5];

export class TimedActivator extends ActivatorBase {
  invert = false;
  useAccelCode = false;
  accelSpeed = 0;
  maxSpeed = 0;
  accel = 0;
  intervalFrames: number[] = [1 / FIXED_DT];
  currentIntervalIndex = 0;
  currentIntervalFrameCounter = 0;
  transformToScale: import('../world.ts').SimGo | null = null;
  scaleX = false;
  scaleY = false;
  startX = 1;
  startY = 1;

  override awake() {
    const d = this.data;
    this.invert = bool(d.invert);
    this.useAccelCode = bool(d.useAccelCode);
    this.accelSpeed = num(d.accelSpeed);
    this.maxSpeed = num(d.maxSpeed);
    this.accel = num(d.accel);
    this.scaleX = bool(d.scaleX);
    this.scaleY = bool(d.scaleY);
  }

  override start() {
    this.refreshIntervalFrames();
    if (this.invert) this.activate();
    this.transformToScale = this.refGo(this.data.transformToScale);
    if (this.transformToScale) {
      const l = this.transformToScale.local;
      this.startX = Math.hypot(l.a, l.b);
      this.startY = Math.hypot(l.c, l.d);
    }
  }

  refreshIntervalFrames() {
    this.currentIntervalIndex = 0;
    this.currentIntervalFrameCounter = 0;
    const custom = String(this.data.customTimerInterval ?? '');
    if (this.useAccelCode) this.intervalFrames = [this.accelSpeed / FIXED_DT];
    else if (custom.length > 0) this.intervalFrames = custom.split(',').map((s) => parseFloat(s.trim()) / FIXED_DT);
    else this.intervalFrames = [(TIMER_SECONDS[num(this.data.timerSpeed)] ?? 1) / FIXED_DT];
  }

  override fixedUpdate() {
    this.currentIntervalFrameCounter++;
    const frames = this.intervalFrames[this.currentIntervalIndex];
    const t = this.transformToScale;
    if (t) {
      const l = t.local;
      const rot = Math.atan2(l.b, l.a);
      let sx = Math.hypot(l.a, l.b), sy = Math.hypot(l.c, l.d);
      if (this.scaleX) sx = (1 - this.currentIntervalFrameCounter / frames) * this.startX;
      if (this.scaleY) sy = (1 - this.currentIntervalFrameCounter / frames) * this.startY;
      const c = Math.cos(rot), s = Math.sin(rot);
      t.setLocal({ a: c * sx, b: s * sx, c: -s * sy, d: c * sy, tx: l.tx, ty: l.ty });
    }
    this.accelSpeed -= this.accel;
    if (this.accelSpeed < this.maxSpeed) this.accelSpeed = this.maxSpeed;
    if (this.currentIntervalFrameCounter >= frames) {
      if (this.useAccelCode) this.refreshIntervalFrames();
      if (this.isActivated) this.deactivate();
      else this.activate();
      this.sound('timerTick');
      this.currentIntervalIndex = (this.currentIntervalIndex + 1) % this.intervalFrames.length;
      this.currentIntervalFrameCounter = 0;
    }
  }
}

export class DashActivator extends ActivatorBase {
  toggle = true;
  coolDownCounter = new FrameCounter();
  playerHasDashed = false;
  desiredState = false;

  override awake() {
    this.toggle = bool(this.data.toggle, true);
    this.coolDownCounter = FrameCounter.from(this.data.coolDownCounter, 0);
  }

  override start() {
    this.game.dashListeners.add(this);
  }

  onPlayerDash(_p: GamePlayer) {
    this.playerHasDashed = true;
  }

  override fixedUpdate() {
    this.coolDownCounter.countCheckFinishReset();
    if (this.playerHasDashed && this.coolDownCounter.isNotRunning()) {
      this.desiredState = this.toggle ? !this.desiredState : true;
      this.coolDownCounter.start();
    } else if (!this.toggle) this.desiredState = false;
    this.playerHasDashed = false;
    if (this.desiredState !== this.isActivated) this.setActiveState(this.desiredState);
  }

  override onDestroy() {
    this.game.dashListeners.delete(this);
  }
}

/** Jump, wall jump, and dash. Same cooldown and toggle rules as DashActivator. */
export class PlayerActionActivator extends ActivatorBase {
  toggle = true;
  activateOnJump = false;
  activateOnWallJump = false;
  activateOnDash = false;
  coolDownCounter = new FrameCounter();
  playerHasJumped = false;
  playerHasWallJumped = false;
  playerHasDashed = false;
  desiredState = false;

  override awake() {
    const d = this.data;
    this.toggle = bool(d.toggle, true);
    this.activateOnJump = bool(d.activateOnJump);
    this.activateOnWallJump = bool(d.activateOnWallJump);
    this.activateOnDash = bool(d.activateOnDash);
    this.coolDownCounter = FrameCounter.from(d.coolDownCounter, 0);
  }

  override start() {
    this.game.dashListeners.add(this);
    this.game.jumpListeners.add(this);
  }

  onPlayerDash(_p: GamePlayer) {
    this.playerHasDashed = true;
  }

  onPlayerJumped(_p: GamePlayer, isWallJump: boolean) {
    if (isWallJump) this.playerHasWallJumped = true;
    else this.playerHasJumped = true;
  }

  override fixedUpdate() {
    this.coolDownCounter.countCheckFinishReset();
    const fired =
      (this.playerHasDashed && this.activateOnDash) ||
      (this.playerHasJumped && this.activateOnJump) ||
      (this.playerHasWallJumped && this.activateOnWallJump);
    if (fired && this.coolDownCounter.isNotRunning()) {
      this.desiredState = this.toggle ? !this.desiredState : true;
      this.coolDownCounter.start();
    } else if (!this.toggle) this.desiredState = false;
    this.playerHasJumped = false;
    this.playerHasWallJumped = false;
    this.playerHasDashed = false;
    if (this.desiredState !== this.isActivated) this.setActiveState(this.desiredState);
  }

  override onDestroy() {
    this.game.dashListeners.delete(this);
    this.game.jumpListeners.delete(this);
  }
}

/** PlatformMovePoint (start point / MoveTo points) as plain data */
export interface MovePoint {
  pos: V2;
  speed: number;
  acc: number;
  returnSpeed: number;
  returnAcc: number;
  pauseFrames: number;
  pausePlatformOnArrival: boolean;
  warpToNextPoint: boolean;
}
